import ExcelJS from "exceljs";

/**
 * Strict user-wise Category + Geo Mapping validation for Add/Edit User and bulk Excel.
 * Category is stored as CATGCODE CSV; geo as UserLocationMapping rows.
 * Category × Geo is treated as a unique combination per user.
 */

export const GEO_DEFAULT = "0000";

const BV_CODE_KEYS = ["catgCode", "CATGCODE", "CatgCode"];
const BV_NAME_KEYS = ["catgName", "CATGNAME", "CatgName", "bvName", "BVName"];

/** Match server: sp_GetCategoryMastersNS returns "MikasaPly (Domestic)". */
function normMasterName(value) {
  let t = String(value || "").trim();
  if (!t) return "";
  t = t.replace(/[\u2010-\u2015\u2212]/g, "-").replace(/\s+/g, " ");
  return t.toUpperCase();
}

function baseMasterName(value) {
  const n = normMasterName(value);
  const idx = n.lastIndexOf(" (");
  if (idx > 0 && n.endsWith(")")) return n.slice(0, idx).trimEnd();
  return n;
}

function categoryMarketCode(row) {
  return String(row?.marketCode || row?.MARKETCODE || row?.MarketCode || "").trim();
}

const MSG = {
  duplicateCategoryGeo:
    "This Category and Geo Mapping is already assigned to this user.",
  regionNotInZone: "The selected Region does not belong to the selected Zone.",
  territoryNotInRegion:
    "The selected Territory does not belong to the selected Region.",
  zoneNotInCountry: "The selected Zone does not belong to the selected Country.",
  regionNotInCountry:
    "The selected Region does not belong to the selected Country.",
  territoryNotInZone:
    "The selected Territory does not belong to the selected Zone.",
  territoryNotInCountry:
    "The selected Territory does not belong to the selected Country.",
  invalidCategory:
    "Invalid Category mapping for the selected Business Vertical.",
  duplicateGeo:
    "Duplicate or conflicting Geo Mapping is not allowed. Do not mix a broader level (e.g. Country with Zone/Region/Territory = All) with more specific Zone/Region/Territory rows for the same area. For whole-country access, select Country only (leave Zone/Region/Territory empty), or select specific territories without keeping Country-All.",
  invalidCountry: "Invalid Country mapping.",
  invalidZone: "Invalid Zone mapping.",
  invalidRegion: "Invalid Region mapping.",
  invalidTerritory: "Invalid Territory mapping.",
  duplicateCategory: "Duplicate Category mapping is not allowed for this user.",
  categoryRequired: "Business Vertical is required",
  countryRequired: "Select at least one Country",
  notFoundCategory: "Business Vertical not found (one or more values).",
  notFoundCountry: "Country not found (one or more values).",
  notFoundZone: "Zone not found (one or more values).",
  notFoundRegion: "Region not found (one or more values).",
  notFoundTerritory: "Territory not found (one or more values).",
  notFoundMarket: "Market not found (one or more values).",
  notFoundRole: "Role not found.",
  notFoundPlant: "Plant not found (one or more values).",
  notFoundRdc: "RDC not found (one or more values).",
  plantRequired: "Select at least one Plant",
  rdcRequired: "Select at least one RDC",
  emailExists: "Email already exists",
  employeeExists: "Employee Code already exists",
  roleScopeTaken:
    "This Role is already assigned to another user for the same Business Vertical and the same Geo location, Plant, or RDC.",
};

export function normalizeGeoCode(value) {
  const v = String(value ?? "").trim();
  if (!v || v.toLowerCase() === "all") return GEO_DEFAULT;
  return v;
}

export function isGeoDefault(value) {
  return normalizeGeoCode(value) === GEO_DEFAULT;
}

export function parseCategoryCodes(raw) {
  if (Array.isArray(raw)) {
    return raw
      .map((c) => String(c ?? "").trim())
      .filter(Boolean);
  }
  if (typeof raw === "string") {
    return raw
      .split(/[,;|]/)
      .map((c) => c.trim())
      .filter(Boolean);
  }
  return [];
}

export function geoMappingKey(mapping) {
  const cc = normalizeGeoCode(mapping?.countryCode ?? mapping?.CountryCode);
  const zc = normalizeGeoCode(mapping?.zoneCode ?? mapping?.ZoneCode);
  const rc = normalizeGeoCode(mapping?.regionCode ?? mapping?.RegionCode);
  const tc = normalizeGeoCode(mapping?.territoryCode ?? mapping?.TerritoryCode);
  return `${cc}|${zc}|${rc}|${tc}`;
}

export function categoryGeoKey(categoryCode, mapping) {
  return `${String(categoryCode || "").trim().toUpperCase()}|${geoMappingKey(mapping)}`;
}

function findByCode(list, code, getters) {
  const sel = String(code ?? "").trim().toUpperCase();
  if (!sel || sel === GEO_DEFAULT) return null;
  return (list || []).find((row) =>
    getters.some((g) => String(g(row) ?? "").trim().toUpperCase() === sel)
  );
}

function mappingCovers(a, b) {
  const levels = [
    ["countryCode", "CountryCode"],
    ["zoneCode", "ZoneCode"],
    ["regionCode", "RegionCode"],
    ["territoryCode", "TerritoryCode"],
  ];
  for (const [camel, pascal] of levels) {
    const av = normalizeGeoCode(a?.[camel] ?? a?.[pascal]);
    const bv = normalizeGeoCode(b?.[camel] ?? b?.[pascal]);
    if (av === GEO_DEFAULT) continue;
    if (bv === GEO_DEFAULT) return false;
    if (av.toUpperCase() !== bv.toUpperCase()) return false;
  }
  return true;
}

/**
 * Validate categories against allowed CATEGORYMASTER rows for the user's market(s).
 * @returns {string|null} error message or null
 */
export function validateUserCategories(categoryCodes, allowedCategories) {
  const codes = parseCategoryCodes(categoryCodes);
  if (!codes.length) return MSG.categoryRequired;

  const seen = new Set();
  for (const code of codes) {
    const key = code.toUpperCase();
    if (seen.has(key)) return MSG.duplicateCategory;
    seen.add(key);
  }

  const allowed = Array.isArray(allowedCategories) ? allowedCategories : [];
  if (!allowed.length) {
    // Masters not loaded yet — still reject empty; skip membership when list unknown.
    return null;
  }

  for (const code of codes) {
    const row = lookupMasterRow(
      allowed,
      code,
      BV_CODE_KEYS,
      BV_NAME_KEYS
    );
    if (!row) return MSG.invalidCategory;
  }
  return null;
}

/**
 * Validate geo hierarchy + duplicates/overlaps for location mapping rows.
 * @returns {string|null}
 */
export function validateUserGeoMappings(
  locationMappings,
  { countries = [], zones = [], regions = [], territories = [] } = {},
  { requireCountry = true } = {}
) {
  const rows = Array.isArray(locationMappings) ? locationMappings : [];
  if (!rows.length) {
    return requireCountry ? MSG.countryRequired : null;
  }

  const seenGeo = new Set();
  for (const m of rows) {
    const key = geoMappingKey(m);
    if (seenGeo.has(key.toUpperCase())) return MSG.duplicateGeo;
    seenGeo.add(key.toUpperCase());

    const cc = normalizeGeoCode(m.countryCode ?? m.CountryCode);
    const zc = normalizeGeoCode(m.zoneCode ?? m.ZoneCode);
    const rc = normalizeGeoCode(m.regionCode ?? m.RegionCode);
    const tc = normalizeGeoCode(m.territoryCode ?? m.TerritoryCode);

    if (cc === GEO_DEFAULT && zc === GEO_DEFAULT && rc === GEO_DEFAULT && tc === GEO_DEFAULT) {
      continue;
    }

    if (cc !== GEO_DEFAULT && countries.length) {
      const country = findByCode(countries, cc, [
        (r) => r.countryCode || r.CountryCode,
      ]);
      if (!country) return MSG.invalidCountry;
    }

    let zoneRow = null;
    if (zc !== GEO_DEFAULT) {
      zoneRow = findByCode(zones, zc, [
        (r) => r.zoneCode || r.ZoneCode,
        (r) => (Array.isArray(r.zoneCodes) ? r.zoneCodes.join(",") : ""),
      ]);
      if (zones.length && !zoneRow) {
        // Also accept when zoneCodes array contains the code
        zoneRow = (zones || []).find((z) =>
          Array.isArray(z.zoneCodes) &&
          z.zoneCodes.some((c) => String(c).trim().toUpperCase() === zc.toUpperCase())
        );
      }
      if (zones.length && !zoneRow) return MSG.invalidZone;

      if (cc !== GEO_DEFAULT && zoneRow) {
        const zoneCountries = [];
        const zcc = zoneRow.countryCode || zoneRow.CountryCode;
        if (zcc) zoneCountries.push(String(zcc).trim().toUpperCase());
        if (Array.isArray(zoneRow.countryCodes)) {
          zoneRow.countryCodes.forEach((c) =>
            zoneCountries.push(String(c).trim().toUpperCase())
          );
        }
        if (
          zoneCountries.length &&
          !zoneCountries.includes(cc.toUpperCase())
        ) {
          return MSG.zoneNotInCountry;
        }
      }
    }

    let regionRow = null;
    if (rc !== GEO_DEFAULT) {
      regionRow = findByCode(regions, rc, [
        (r) => r.regionCode || r.RegionCode,
      ]);
      if (regions.length && !regionRow) return MSG.invalidRegion;

      if (regionRow) {
        const rZone = String(regionRow.zoneCode || regionRow.ZoneCode || "").trim();
        const rCountry = String(
          regionRow.countryCode || regionRow.CountryCode || ""
        ).trim();
        if (zc !== GEO_DEFAULT && rZone && rZone.toUpperCase() !== zc.toUpperCase()) {
          return MSG.regionNotInZone;
        }
        if (
          cc !== GEO_DEFAULT &&
          rCountry &&
          rCountry.toUpperCase() !== cc.toUpperCase()
        ) {
          return MSG.regionNotInCountry;
        }
      }
    }

    if (tc !== GEO_DEFAULT) {
      const territoryRow = findByCode(territories, tc, [
        (r) => r.territoryCode || r.TerritoryCode,
      ]);
      if (territories.length && !territoryRow) return MSG.invalidTerritory;

      if (territoryRow) {
        const tRegion = String(
          territoryRow.regionCode || territoryRow.RegionCode || ""
        ).trim();
        const tZone = String(
          territoryRow.zoneCode || territoryRow.ZoneCode || ""
        ).trim();
        const tCountry = String(
          territoryRow.countryCode || territoryRow.CountryCode || ""
        ).trim();
        if (
          rc !== GEO_DEFAULT &&
          tRegion &&
          tRegion.toUpperCase() !== rc.toUpperCase()
        ) {
          return MSG.territoryNotInRegion;
        }
        if (
          zc !== GEO_DEFAULT &&
          tZone &&
          tZone.toUpperCase() !== zc.toUpperCase()
        ) {
          return MSG.territoryNotInZone;
        }
        if (
          cc !== GEO_DEFAULT &&
          tCountry &&
          tCountry.toUpperCase() !== cc.toUpperCase()
        ) {
          return MSG.territoryNotInCountry;
        }
      }
    }
  }

  // Overlapping / ancestor-descendant conflicts
  for (let i = 0; i < rows.length; i++) {
    for (let j = i + 1; j < rows.length; j++) {
      const a = rows[i];
      const b = rows[j];
      if (geoMappingKey(a).toUpperCase() === geoMappingKey(b).toUpperCase()) {
        return MSG.duplicateGeo;
      }
      if (mappingCovers(a, b) || mappingCovers(b, a)) {
        return MSG.duplicateGeo;
      }
    }
  }

  return null;
}

/**
 * Category + Geo uniqueness for the final user mapping set.
 * @param {string[]|string} categoryCodes
 * @param {object[]} locationMappings
 * @param {number|string|null} excludeMappingId - ignored for full-replace saves; kept for API symmetry
 * @returns {string|null}
 */
export function validateCategoryGeoCombinations(
  categoryCodes,
  locationMappings,
  excludeMappingId = null
) {
  const cats = parseCategoryCodes(categoryCodes);
  const rows = (Array.isArray(locationMappings) ? locationMappings : []).filter(
    (m) => {
      if (excludeMappingId == null || excludeMappingId === "") return true;
      const id = m.mappingId ?? m.MappingId;
      return String(id) !== String(excludeMappingId);
    }
  );

  if (!cats.length) return MSG.categoryRequired;

  // Plant/RDC (no geo rows): category uniqueness only
  if (!rows.length) {
    const seen = new Set();
    for (const c of cats) {
      const k = c.toUpperCase();
      if (seen.has(k)) return MSG.duplicateCategory;
      seen.add(k);
    }
    return null;
  }

  const seen = new Set();
  for (const cat of cats) {
    for (const m of rows) {
      const key = categoryGeoKey(cat, m);
      if (seen.has(key)) return MSG.duplicateCategoryGeo;
      seen.add(key);
    }
  }
  return null;
}

/**
 * Full validation for Add/Edit User save payload.
 */
export function validateUserCategoryAndGeo({
  categoryCodes,
  locationMappings,
  allowedCategories,
  geoMasters,
  isGeneralUser,
  excludeMappingId = null,
}) {
  const catErr = validateUserCategories(categoryCodes, allowedCategories);
  if (catErr) return catErr;

  if (isGeneralUser) {
    const geoErr = validateUserGeoMappings(locationMappings, geoMasters || {}, {
      requireCountry: true,
    });
    if (geoErr) return geoErr;
  }

  const comboErr = validateCategoryGeoCombinations(
    categoryCodes,
    isGeneralUser ? locationMappings : [],
    excludeMappingId
  );
  if (comboErr) return comboErr;

  return null;
}

/** Normalize Excel header for flexible template matching. */
export function normalizeExcelHeader(value) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toUpperCase();
}

/** Safely read display text from an ExcelJS cell (handles richText / hyperlink / formula). */
export function getExcelCellText(cell) {
  if (!cell) return "";
  try {
    if (cell.text != null && String(cell.text).trim() !== "") {
      return String(cell.text).trim();
    }
  } catch {
    // cell.text can throw for some formula/error cells
  }

  const v = cell.value;
  if (v == null || v === "") return "";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
    return String(v).trim();
  }
  if (v instanceof Date) {
    return v.toISOString();
  }
  if (typeof v === "object") {
    if (Array.isArray(v.richText)) {
      return v.richText
        .map((p) => (p && p.text != null ? String(p.text) : ""))
        .join("")
        .trim();
    }
    if (v.text != null) return String(v.text).trim();
    if (v.result != null) {
      if (typeof v.result === "object" && v.result.text != null) {
        return String(v.result.text).trim();
      }
      return String(v.result).trim();
    }
    if (v.hyperlink != null && v.text == null) return String(v.hyperlink).trim();
    if (v.sharedFormula != null || v.formula != null) {
      try {
        return cell.text != null ? String(cell.text).trim() : "";
      } catch {
        return "";
      }
    }
  }
  try {
    return String(v).trim();
  } catch {
    return "";
  }
}

/** Same column set as /csrp/users grid Excel/CSV download. */
export const USER_MANAGEMENT_EXCEL_HEADERS = [
  "SNo.",
  "Employee Code",
  "AD ID",
  "User Full Name",
  "Email",
  "Mobile Number",
  "Role",
  "Market",
  "Country",
  "Zone",
  "Region",
  "Territory",
  "Business Vertical",
  "User Type",
  "Plant / RDC",
  "Created On",
  "Status",
];

/** Columns required for bulk upload (subset of grid export). */
export const USER_BULK_UPLOAD_REQUIRED_HEADERS = [
  "AD ID",
  "USER FULL NAME",
  "EMAIL",
  "ROLE",
  "MARKET",
  "COUNTRY",
  "BUSINESS VERTICAL",
];

/**
 * Resolve column index for known aliases.
 * Canonical names match /csrp/users grid download headers.
 */
export function resolveBulkHeaderMap(headerMap) {
  const map = { ...headerMap };
  const entries = Object.entries(headerMap || {});

  const findAlias = (predicates) => {
    for (const [key, col] of entries) {
      if (predicates.some((p) => p(key))) return col;
    }
    return null;
  };

  const aliases = {
    "SNO.": [(k) => k === "SNO." || k === "SNO" || k === "S.NO." || k === "SL NO" || k === "SL.NO."],
    "EMPLOYEE CODE": [
      (k) => k === "EMPLOYEE CODE" || k === "EMPLOYEECODE" || k === "EMP CODE",
    ],
    "AD ID": [(k) => k === "AD ID" || k === "ADID" || k === "USERNAME" || k === "USER NAME"],
    "USER FULL NAME": [(k) => k === "USER FULL NAME" || k === "FULL NAME" || k === "FULLNAME"],
    EMAIL: [
      (k) =>
        k === "EMAIL" ||
        k === "EMAIL ID" ||
        k === "EMAILID" ||
        k === "EMPLOYEE EMAIL ID" ||
        k === "EMPLOYEE EMAIL",
    ],
    "MOBILE NUMBER": [
      (k) => k === "MOBILE NUMBER" || k === "MOBILE" || k === "PHONE" || k === "PHONE NUMBER",
    ],
    ROLE: [
      (k) =>
        k === "ROLE" ||
        k === "USER TYPE CODE" ||
        k === "USERTYPECODE" ||
        k === "USER TYPE NAME" ||
        k === "USERTYPE",
    ],
    MARKET: [(k) => k === "MARKET" || k === "MARKET CODE" || k === "MARKETCODE"],
    COUNTRY: [(k) => k === "COUNTRY" || k === "COUNTRY CODE" || k === "COUNTRYCODE"],
    ZONE: [(k) => k === "ZONE" || k === "ZONE CODE" || k === "ZONECODE"],
    REGION: [(k) => k === "REGION" || k === "REGION CODE" || k === "REGIONCODE"],
    TERRITORY: [
      (k) => k === "TERRITORY" || k === "TERRITORY CODE" || k === "TERRITORYCODE",
    ],
    "BUSINESS VERTICAL": [
      (k) => k === "BUSINESS VERTICAL",
      (k) => k.includes("BUSINESS VERTICAL"),
      (k) => k === "CATEGORY" || k === "CATGCODE" || k === "CATG NAME" || k === "BV",
      (k) => k.includes("CHECKLIST") && k.includes("VERTICAL"),
    ],
    "USER TYPE": [
      (k) =>
        k === "USER TYPE" ||
        k === "USER SPECIFICATION TYPE" ||
        k === "USER SPECIFICATION" ||
        k === "USERSPECIFICATIONTYPE" ||
        k === "SPECIFICATION TYPE",
    ],
    "PLANT / RDC": [
      (k) =>
        k === "PLANT / RDC" ||
        k === "PLANT/RDC" ||
        k === "PLANT RDC" ||
        k === "PLANT" ||
        k === "RDC" ||
        k === "PLANTIDS" ||
        k === "RDCIDS",
    ],
    "CREATED ON": [(k) => k === "CREATED ON" || k === "CREATEDON" || k === "CREATED DATE"],
    STATUS: [(k) => k === "STATUS" || k === "ISACTIVE" || k === "ACTIVE"],
  };

  for (const [canonical, preds] of Object.entries(aliases)) {
    if (map[canonical] == null) {
      const col = findAlias(preds);
      if (col != null) map[canonical] = col;
    }
  }

  // Dual-key: new header is User Type; old Excel used User Specification Type
  if (map["USER TYPE"] != null && map["USER SPECIFICATION TYPE"] == null) {
    map["USER SPECIFICATION TYPE"] = map["USER TYPE"];
  } else if (map["USER SPECIFICATION TYPE"] != null && map["USER TYPE"] == null) {
    map["USER TYPE"] = map["USER SPECIFICATION TYPE"];
  }

  // Backward compat: old template long BV header
  if (map["BUSINESS VERTICAL"] != null) {
    map["BUSINESS VERTICAL AS PER CHEKLIST NOT ACTUAL VERTICAL (TBD)"] =
      map["BUSINESS VERTICAL"];
  }

  return map;
}

/**
 * Find the header row in an ExcelJS worksheet (supports grid export layout:
 * title on row 1, headers on row 3).
 */
export function findBulkExcelHeaderRow(ws, maxScanRows = 10) {
  if (!ws) return null;
  const rowCount = Math.max(ws.rowCount || 0, ws.actualRowCount || 0, maxScanRows);
  const max = Math.min(rowCount, maxScanRows);
  for (let r = 1; r <= max; r++) {
    const row = ws.getRow(r);
    const headerMap = {};
    // Use a wide column scan — eachCell can miss styled/merged header cells on some files
    const colCount = Math.max(row.cellCount || 0, ws.columnCount || 0, 30);
    for (let c = 1; c <= colCount; c++) {
      const cell = row.getCell(c);
      const v = normalizeExcelHeader(getExcelCellText(cell));
      if (v && headerMap[v] == null) headerMap[v] = c;
    }
    const resolved = resolveBulkHeaderMap(headerMap);
    if (resolved["AD ID"] != null && resolved["USER FULL NAME"] != null) {
      return { rowNumber: r, headerMap: resolved };
    }
  }
  return null;
}

/**
 * Strong client-side bulk row validation before submit.
 * Master membership / hierarchy are re-checked on the server.
 */
export const MASTER_USER_EXCEL_HEADERS = [
  "Employee_code",
  "FullName",
  "RoleName",
  "User Type",
  "email",
  "Market Type",
  "Country",
  "Zone",
  "Region",
  "Territory",
  "Plant",
  "BusinessVertical",
  "Active",
];

export const MASTER_BULK_UPLOAD_REQUIRED_HEADERS = [
  "EMAIL",
  "FULLNAME",
  "ROLENAME",
  "MARKET TYPE",
  "COUNTRY",
  "BUSINESSVERTICAL",
];

export function resolveMasterHeaderMap(headerMap) {
  const map = { ...headerMap };
  const entries = Object.entries(headerMap || {});
  const findAlias = (predicates) => {
    for (const [key, col] of entries) {
      if (predicates.some((p) => p(key))) return col;
    }
    return null;
  };
  const aliases = {
    USERID: [(k) => k === "USERID" || k === "USER ID" || k === "USER_ID"],
    ROLEID: [(k) => k === "ROLEID" || k === "ROLE ID" || k === "ROLE_ID"],
    ROLENAME: [(k) => k === "ROLENAME" || k === "ROLE NAME" || k === "ROLE"],
    "USER TYPE": [
      (k) => k === "USER TYPE" || k === "USERTYPE" || k === "USER_TYPE",
      (k) => k === "USER SPECIFICATION TYPE" || k === "USERSPECIFICATIONTYPE",
    ],
    FIRSTNAME: [(k) => k === "FIRSTNAME" || k === "FIRST NAME"],
    LASTNAME: [(k) => k === "LASTNAME" || k === "LAST NAME"],
    FULLNAME: [(k) => k === "FULLNAME" || k === "FULL NAME" || k === "USER FULL NAME"],
    EMAIL: [(k) => k === "EMAIL" || k === "EMAIL ID" || k === "EMAILID"],
    "ACTIVE/INACTIVE": [
      (k) => k === "ACTIVE" || k === "ACTIVE/INACTIVE" || k === "STATUS",
      (k) => k.includes("ACTIVE/INACTIVE") || k.includes("ACTIVE"),
    ],
    EMPLOYEE_CODE: [
      (k) =>
        k === "EMPLOYEE_CODE" ||
        k === "EMPLOYEE CODE" ||
        k === "EMPLOYEECODE" ||
        k === "EMP CODE",
    ],
    COUNTRY: [(k) => k === "COUNTRY"],
    "MARKET TYPE": [(k) => k === "MARKET TYPE" || k === "MARKET" || k === "MARKETTYPE"],
    ZONE: [(k) => k === "ZONE"],
    REGION: [(k) => k === "REGION"],
    TERRITORY: [(k) => k === "TERRITORY"],
    PLANT: [
      (k) => k === "PLANT",
      (k) =>
        k === "PLANT/RDC" ||
        k === "PLANT / RDC" ||
        k === "PLANT RDC" ||
        k === "PLANT_RDC" ||
        k === "RDC",
    ],
    BUSINESSVERTICAL: [
      (k) => k.includes("NEW CSRP") && k.includes("BUSINESSVERTICAL"),
      (k) => k.includes("NEW CSRP") && k.includes("BUSINESS VERTICAL"),
      (k) => k === "BUSINESSVERTICAL" || k === "BUSINESS VERTICAL",
      (k) => k.includes("BUSINESS VERTICAL") || k.includes("BUSINESSVERTICAL"),
    ],
  };
  for (const [canonical, preds] of Object.entries(aliases)) {
    if (map[canonical] == null) {
      const col = findAlias(preds);
      if (col != null) map[canonical] = col;
    }
  }
  const newCsrpBv = findAlias([
    (k) => k.includes("NEW CSRP") && (k.includes("BUSINESSVERTICAL") || k.includes("BUSINESS VERTICAL")),
  ]);
  if (newCsrpBv != null) map.BUSINESSVERTICAL = newCsrpBv;
  return map;
}

export function findMasterExcelHeaderRow(ws, maxScanRows = 10) {
  if (!ws) return null;
  const rowCount = Math.max(ws.rowCount || 0, ws.actualRowCount || 0, maxScanRows);
  const max = Math.min(rowCount, maxScanRows);
  for (let r = 1; r <= max; r++) {
    const row = ws.getRow(r);
    const headerMap = {};
    const colCount = Math.max(row.cellCount || 0, ws.columnCount || 0, 20);
    for (let c = 1; c <= colCount; c++) {
      const cell = row.getCell(c);
      const v = normalizeExcelHeader(getExcelCellText(cell));
      if (v && headerMap[v] == null) headerMap[v] = c;
    }
    const resolved = resolveMasterHeaderMap(headerMap);
    if (resolved.EMAIL != null && (resolved.FULLNAME != null || resolved.ROLENAME != null)) {
      return { rowNumber: r, headerMap: resolved };
    }
  }
  return null;
}

function lookupMasterRow(list, token, codeKeys, nameKeys, preferredMarketCodes) {
  const t = String(token || "").trim();
  if (!t) return null;
  const tokUpper = t.toUpperCase();
  const tokNorm = normMasterName(t);
  const tokBase = baseMasterName(t);
  const matches = (list || []).filter((row) => {
    if (
      (codeKeys || []).some(
        (k) => String(row?.[k] ?? "").trim().toUpperCase() === tokUpper
      )
    ) {
      return true;
    }
    return (nameKeys || []).some((k) => {
      const name = String(row?.[k] ?? "");
      const full = normMasterName(name);
      const bas = baseMasterName(name);
      return (
        full === tokNorm ||
        bas === tokNorm ||
        bas === tokBase ||
        full === tokBase
      );
    });
  });
  if (!matches.length) return null;
  const prefs = (preferredMarketCodes || [])
    .map((c) => String(c || "").trim().toUpperCase())
    .filter(Boolean);
  if (prefs.length) {
    const forMarket = matches.find((row) =>
      prefs.includes(categoryMarketCode(row).toUpperCase())
    );
    if (forMarket) return forMarket;
  }
  return matches[0];
}

/**
 * Resolve Excel RoleName against live UserTypeMaster only (no hardcoded aliases).
 * Accepts UserTypeCode or UserTypeName (case-insensitive).
 * @returns {{ userTypeCode: string, userTypeName: string } | null}
 */
export function resolveUserTypeFromMaster(userTypes, roleToken) {
  const row = lookupMasterRow(
    userTypes,
    roleToken,
    ["userTypeCode", "UserTypeCode", "USERTYPECODE"],
    ["userTypeName", "UserTypeName", "USERTYPENAME"]
  );
  if (!row) return null;
  const userTypeCode =
    String(row.userTypeCode ?? row.UserTypeCode ?? row.USERTYPECODE ?? "").trim();
  const userTypeName =
    String(row.userTypeName ?? row.UserTypeName ?? row.USERTYPENAME ?? "").trim();
  if (!userTypeCode && !userTypeName) return null;
  return {
    userTypeCode: userTypeCode || userTypeName,
    userTypeName: userTypeName || userTypeCode,
  };
}

/** Map Excel/form "MikasaPly" (or a CATGCODE) to CATEGORYMASTER.CATGCODE. */
export function resolveBusinessVerticalCodes(tokens, categories, marketCodes) {
  const list = Array.isArray(tokens) ? tokens : parseCategoryCodes(tokens);
  if (!list.length) return [];
  return list
    .map((tok) => {
      const row = lookupMasterRow(
        categories,
        tok,
        BV_CODE_KEYS,
        BV_NAME_KEYS,
        marketCodes
      );
      if (!row) return String(tok || "").trim();
      return parentCodeOf(row, BV_CODE_KEYS) || String(tok || "").trim();
    })
    .filter(Boolean);
}

function parentCodeOf(row, keys) {
  for (const k of keys) {
    const v = String(row?.[k] ?? "").trim();
    if (v && v !== GEO_DEFAULT) return v;
  }
  return "";
}

function childrenBelongToParents(childRows, parentCodes, parentKeysOnChild) {
  if (!Array.isArray(parentCodes) || !parentCodes.length) return true;
  const set = new Set(parentCodes.map((c) => String(c).trim().toUpperCase()).filter(Boolean));
  if (!set.size) return true;
  for (const child of childRows || []) {
    const pc = parentCodeOf(child, parentKeysOnChild);
    if (!pc) continue;
    if (!set.has(pc.toUpperCase())) return false;
  }
  return true;
}

function indexExistingUsers(list) {
  const byAd = new Map();
  const byEmail = new Map();
  const byEmp = new Map();
  for (const u of Array.isArray(list) ? list : []) {
    const rec = {
      id: String(u.userIdCode || u.UserIdCode || "").trim(),
      ad: String(u.userName || u.UserName || "").trim(),
      email: String(u.email || u.Email || "").trim(),
      emp: String(u.employeeCode || u.EmployeeCode || "").trim(),
      market: String(u.marketCode || u.MarketCode || "").trim(),
      catg: String(u.catgCode || u.CATGCODE || u.bvCode || u.BVCode || "").trim(),
    };
    if (rec.ad) byAd.set(rec.ad.toUpperCase(), rec);
    if (rec.email) byEmail.set(rec.email.toUpperCase(), rec);
    if (rec.emp) byEmp.set(rec.emp.toUpperCase(), rec);
  }
  return { byAd, byEmail, byEmp };
}

function isSameExistingUser(a, b) {
  if (!a || !b) return false;
  if (a.id && b.id) return a.id.toUpperCase() === b.id.toUpperCase();
  if (a.ad && b.ad) return a.ad.toUpperCase() === b.ad.toUpperCase();
  return false;
}

function specificGeoTokens(raw) {
  const tokens = parseCategoryCodes(raw).map((t) => normalizeGeoCode(t));
  const specific = tokens.filter((t) => t !== GEO_DEFAULT);
  return specific.length ? specific : tokens.filter((t) => t === GEO_DEFAULT).slice(0, 1);
}

function lookupCode(list, token, codeKeys, nameKeys) {
  const row = lookupMasterRow(list, token, codeKeys, nameKeys);
  return row ? parentCodeOf(row, codeKeys) : String(token || "").trim();
}

function mappingsFromGeoFields(country, zone, region, territory, masters) {
  const countries = masters?.countries || [];
  const zones = masters?.zones || [];
  const regions = masters?.regions || [];
  const territories = masters?.territories || [];
  const terrTokens = specificGeoTokens(territory);
  const rows = [];
  if (terrTokens.length && terrTokens.some((t) => t !== GEO_DEFAULT) && territories.length) {
    for (const tok of terrTokens) {
      if (tok === GEO_DEFAULT) continue;
      const found = lookupMasterRow(
        territories,
        tok,
        ["territoryCode", "TerritoryCode", "TERRITORYCODE"],
        ["territoryName", "TerritoryName", "TERRITORYNAME"]
      );
      if (!found) continue;
      rows.push({
        countryCode: normalizeGeoCode(found.countryCode || found.CountryCode),
        zoneCode: normalizeGeoCode(found.zoneCode || found.ZoneCode),
        regionCode: normalizeGeoCode(found.regionCode || found.RegionCode),
        territoryCode: normalizeGeoCode(found.territoryCode || found.TerritoryCode),
      });
    }
    if (rows.length) return rows;
  }
  const c0 = specificGeoTokens(country)[0] || GEO_DEFAULT;
  const z0 = specificGeoTokens(zone)[0] || GEO_DEFAULT;
  const r0 = specificGeoTokens(region)[0] || GEO_DEFAULT;
  const cc = lookupCode(countries, c0, ["countryCode", "CountryCode"], ["countryName", "CountryName"]);
  const zz = lookupCode(zones, z0, ["zoneCode", "ZoneCode"], ["zoneName", "ZoneName"]);
  const rr = lookupCode(regions, r0, ["regionCode", "RegionCode"], ["regionName", "RegionName"]);
  if (terrTokens.some((t) => t !== GEO_DEFAULT)) {
    for (const tok of terrTokens) {
      if (tok === GEO_DEFAULT) continue;
      rows.push({
        countryCode: normalizeGeoCode(cc),
        zoneCode: normalizeGeoCode(zz),
        regionCode: normalizeGeoCode(rr),
        territoryCode: normalizeGeoCode(tok),
      });
    }
    return rows;
  }
  return [
    {
      countryCode: normalizeGeoCode(cc),
      zoneCode: normalizeGeoCode(zz),
      regionCode: normalizeGeoCode(rr),
      territoryCode: GEO_DEFAULT,
    },
  ];
}

export function bvKeysFromCodes(codes, categories) {
  const keys = [];
  for (const raw of codes || []) {
    const token = String(raw || "").trim();
    if (!token) continue;
    const row = lookupMasterRow(categories || [], token, BV_CODE_KEYS, BV_NAME_KEYS);
    const name = baseMasterName(row ? row.catgName || row.CATGNAME || row.CatgName || token : token);
    keys.push((name || token).toUpperCase());
  }
  return [...new Set(keys)];
}

function scopeTokens(raw) {
  return parseCategoryCodes(raw)
    .map((t) => String(t || "").trim())
    .filter((t) => t && t !== "-" && t.toLowerCase() !== "n/a" && t.toLowerCase() !== "na");
}

/**
 * Same Role + Business Vertical cannot be held by two users for the same
 * geo location, or the same Plant code, or the same RDC code.
 * @returns {string|null}
 */
export function findRoleScopeConflict(candidate, claims) {
  const incoming = candidate?.slots || [];
  for (const a of incoming) {
    for (const b of claims || []) {
      if (String(a.roleKey).toUpperCase() !== String(b.roleKey).toUpperCase()) continue;
      if (String(a.bvKey).toUpperCase() !== String(b.bvKey).toUpperCase()) continue;
      const sameId = a.ownerId && b.ownerId && String(a.ownerId).toUpperCase() === String(b.ownerId).toUpperCase();
      const sameAd = a.ownerAd && b.ownerAd && String(a.ownerAd).toUpperCase() === String(b.ownerAd).toUpperCase();
      if (sameId || sameAd) continue;
      let hit = false;
      if (a.kind === "GEO" && b.kind === "GEO") {
        hit = mappingCovers(a.geo, b.geo) || mappingCovers(b.geo, a.geo);
      } else if (a.kind === b.kind && (a.kind === "PLANT" || a.kind === "RDC")) {
        hit = String(a.code || "").toUpperCase() === String(b.code || "").toUpperCase() && !!a.code;
      }
      if (!hit) continue;
      const who = b.ownerName || b.ownerAd || "another user";
      const role = b.roleLabel || b.roleKey;
      const where = b.rowNumber ? ` (also on row ${b.rowNumber})` : "";
      const scope = a.kind === "PLANT" ? "Plant" : a.kind === "RDC" ? "RDC" : "Geo location";
      return `${MSG.roleScopeTaken} ${role} ${who} already has this Business Vertical and ${scope}${where}.`;
    }
  }
  return null;
}

/** Initiator (UT0000001): many users may share the same Business Vertical and Geo / Plant / RDC. */
export const INITIATOR_USER_TYPE_CODE = "UT0000001";

export function isRoleScopeExempt(roleKey, roleLabel) {
  const key = String(roleKey || "").trim().toUpperCase();
  const label = String(roleLabel || "").trim().toUpperCase();
  return key === INITIATOR_USER_TYPE_CODE || key === "INITIATOR" || label === "INITIATOR";
}

export function buildRoleScopeSlots({
  roleKey,
  roleLabel,
  bvKeys,
  userSpec,
  mappings,
  plantCodes,
  rdcCodes,
  ownerId,
  ownerAd,
  ownerName,
  rowNumber,
}) {
  const role = String(roleKey || "").trim().toUpperCase();
  if (!role) return [];
  if (isRoleScopeExempt(role, roleLabel)) return [];
  const spec = String(userSpec || "").trim();
  const isPlant = /^plant$/i.test(spec);
  const isRdc = /^rdc$/i.test(spec);
  const slots = [];
  const owner = {
    roleKey: role,
    roleLabel: roleLabel || roleKey,
    ownerId: ownerId || "",
    ownerAd: ownerAd || "",
    ownerName: ownerName || "",
    rowNumber: rowNumber || 0,
  };
  for (const bvKey of bvKeys || []) {
    const bv = String(bvKey || "").trim().toUpperCase();
    if (!bv) continue;
    if (isPlant) {
      for (const code of plantCodes || []) {
        slots.push({ ...owner, bvKey: bv, kind: "PLANT", code: String(code).trim().toUpperCase() });
      }
    } else if (isRdc) {
      for (const code of rdcCodes || []) {
        slots.push({ ...owner, bvKey: bv, kind: "RDC", code: String(code).trim().toUpperCase() });
      }
    } else {
      for (const geo of mappings || []) {
        slots.push({ ...owner, bvKey: bv, kind: "GEO", geo });
      }
    }
  }
  return slots;
}

export function slotsFromListedUser(u, masters) {
  if (u.isDeleted === true || u.IsDeleted === true) return [];
  const spec = String(u.userSpecificationType || u.UserSpecificationType || "General").trim();
  const roleKey = String(u.userTypeCode || u.UserTypeCode || u.userTypeName || u.UserTypeName || "").trim();
  const roleLabel = String(u.userTypeName || u.UserTypeName || roleKey).trim();
  const bvCodes = parseCategoryCodes(u.catgCode || u.CATGCODE || u.bvCode || u.BVCode);
  const bvKeys = bvKeysFromCodes(bvCodes, masters?.businessVerticals);
  const isPlant = /^plant$/i.test(spec);
  const isRdc = /^rdc$/i.test(spec);
  const plantCodes = [
    ...scopeTokens(u.plantIds || u.PlantIds),
    ...scopeTokens(u.plantNames || u.PlantNames),
  ];
  const rdcCodes = [
    ...scopeTokens(u.rdcIds || u.RdcIds),
    ...scopeTokens(u.rdcNames || u.RdcNames),
    ...(isRdc ? scopeTokens(u.plantIds || u.PlantIds) : []),
  ];
  const nameAll = [u.countryName, u.CountryName, u.zoneName, u.ZoneName, u.regionName, u.RegionName, u.territoryName, u.TerritoryName]
    .some((v) => String(v || "").trim().toUpperCase() === "ALL");
  let mappings = isPlant || isRdc
    ? []
    : mappingsFromGeoFields(
        u.countryCode || u.CountryCode,
        u.zoneCode || u.ZoneCode,
        u.regionCode || u.RegionCode,
        u.territoryCode || u.TerritoryCode,
        masters
      );
  if (!nameAll && mappings.every((m) =>
    isGeoDefault(m.countryCode) && isGeoDefault(m.zoneCode) &&
    isGeoDefault(m.regionCode) && isGeoDefault(m.territoryCode))) {
    mappings = [];
  }
  return buildRoleScopeSlots({
    roleKey,
    roleLabel,
    bvKeys,
    userSpec: spec,
    mappings,
    plantCodes,
    rdcCodes,
    ownerId: u.userIdCode || u.UserIdCode,
    ownerAd: u.userName || u.UserName,
    ownerName: u.fullName || u.FullName || u.displayName || u.DisplayName,
  });
}

function resolveNamedTokens(
  list,
  raw,
  codeKeys,
  nameKeys,
  isAllFn,
  preferredMarketCodes
) {
  if (!Array.isArray(list) || !list.length) {
    return { skipped: true, rows: [], missing: [], codes: [] };
  }
  const tokens = String(raw || "")
    .split(/[,;|]/)
    .map((s) => s.trim())
    .filter(Boolean);
  const rows = [];
  const missing = [];
  for (const tok of tokens) {
    if (isAllFn(tok)) continue;
    const found = lookupMasterRow(
      list,
      tok,
      codeKeys,
      nameKeys,
      preferredMarketCodes
    );
    if (!found) missing.push(tok);
    else rows.push(found);
  }
  const codes = rows
    .map((r) => parentCodeOf(r, codeKeys))
    .filter(Boolean);
  return { skipped: false, rows, missing, codes };
}

/**
 * Strong client-side bulk row validation before submit.
 * Required fields match Add User; Business Vertical and geo membership / hierarchy
 * use the same masters as the form when options.masters is provided.
 * Server still re-checks on submit.
 * @param {object} [options]
 * @param {boolean} [options.allowMultiGeoRows] Same email/AD ID may appear on multiple geo rows.
 * @param {object} [options.masters] Loaded markets, categories, countries, zones, regions, territories, plants, rdcs, userTypes.
 * @param {Array} [options.existingUsers] Current UserMaster rows — updates use the same BV/geo rules as insert.
 */
export function validateBulkUserRows(rows, options = {}) {
  const list = Array.isArray(rows) ? rows : [];
  const allowMultiGeoRows = options.allowMultiGeoRows === true;
  const masters = options.masters && typeof options.masters === "object" ? options.masters : null;
  const existingIndex = indexExistingUsers(options.existingUsers);
  const errors = [];
  const comboSeen = new Map();
  const adIdSeen = new Map();
  const empSeen = new Map();
  const emailSeen = new Map();
  const uploadAds = new Set(
    list.map((r) => String(r.adId || "").trim().toUpperCase()).filter(Boolean)
  );
  const occupiedScopes = [];
  for (const existingUser of options.existingUsers || []) {
    const ad = String(existingUser.userName || existingUser.UserName || "")
      .trim()
      .toUpperCase();
    if (ad && uploadAds.has(ad)) continue;
    occupiedScopes.push(...slotsFromListedUser(existingUser, masters));
  }

  const isValidEmail = (v) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());

  const splitMulti = (raw) =>
    String(raw || "")
      .split(/[,;|]/)
      .map((s) => s.trim())
      .filter(Boolean);

  const isExplicitAll = (v) => {
    const s = String(v || "").trim().toUpperCase();
    return s === "ALL" || s === "*" || s === GEO_DEFAULT;
  };

  for (const row of list) {
    const rowNumber = row.rowNumber ?? 0;
    const adId = String(row.adId || "").trim();
    const fullName = String(row.fullName || "").trim();
    const email = String(row.email || "").trim();
    const role = String(row.role || "").trim();
    const market = String(row.market || "").trim();
    let country = String(row.country || "").trim();
    const zone = String(row.zone || "").trim();
    const region = String(row.region || "").trim();
    const territory = String(row.territory || "").trim();
    const bv = String(row.businessVertical || "").trim();
    const emp = String(row.employeeCode || "").trim();
    const userSpec = String(row.userSpecificationType || "").trim();
    const isPlantOrRdc =
      /^plant$/i.test(userSpec) || /^rdc$/i.test(userSpec);
    const msgs = [];

    // Export / ALL-country: empty country with lower geo → treat as ALL (valid)
    if (
      !isPlantOrRdc &&
      !country &&
      (zone || region || territory)
    ) {
      country = "ALL";
    }

    if (!adId) msgs.push("AD ID is required.");
    if (!fullName) msgs.push("User Full Name is required.");
    if (!email) msgs.push("Email is required.");
    else if (!isValidEmail(email)) msgs.push("Email format is invalid.");
    if (!role) msgs.push("Role is required.");
    if (!market) msgs.push("Market is required.");
    // Country required only when blank (ALL is allowed)
    if (!isPlantOrRdc && !country) msgs.push("Country is required.");
    if (!bv) msgs.push(MSG.categoryRequired);

    const existing = adId ? existingIndex.byAd.get(adId.toUpperCase()) || null : null;
    row.bulkAction = existing ? "Update" : "Insert";

    if (email && isValidEmail(email)) {
      const emailOwner = existingIndex.byEmail.get(email.toUpperCase());
      if (emailOwner && !isSameExistingUser(emailOwner, existing)) {
        msgs.push(`${MSG.emailExists}: ${email}`);
      }
    }
    if (emp) {
      const empOwner = existingIndex.byEmp.get(emp.toUpperCase());
      if (empOwner && !isSameExistingUser(empOwner, existing)) {
        msgs.push(`${MSG.employeeExists}: ${emp}`);
      }
    }

    if (!allowMultiGeoRows && adId) {
      if (adIdSeen.has(adId.toUpperCase())) {
        msgs.push(
          `Duplicate AD ID in upload (also on row ${adIdSeen.get(adId.toUpperCase())}).`
        );
      } else {
        adIdSeen.set(adId.toUpperCase(), rowNumber);
      }
    }

    if (!allowMultiGeoRows && email && isValidEmail(email)) {
      const ek = email.toUpperCase();
      if (emailSeen.has(ek)) {
        msgs.push(`Duplicate Email in upload (also on row ${emailSeen.get(ek)}).`);
      } else {
        emailSeen.set(ek, rowNumber);
      }
    }

    if (!allowMultiGeoRows && emp) {
      const ek = emp.toUpperCase();
      if (empSeen.has(ek)) {
        msgs.push(
          `Duplicate Employee Code in upload (also on row ${empSeen.get(ek)}).`
        );
      } else {
        empSeen.set(emp.toUpperCase(), rowNumber);
      }
    }

    // Dedupe BV tokens (template may repeat names)
    const cats = [
      ...new Set(parseCategoryCodes(bv).map((c) => c.trim()).filter(Boolean)),
    ];
    if (bv && !cats.length) msgs.push(MSG.invalidCategory);

    // Reject rows that only contain separators / blank multi-values
    for (const [label, raw] of [
      ["Country", country],
      ["Zone", zone],
      ["Region", region],
      ["Territory", territory],
      ["Business Vertical", bv],
    ]) {
      if (!raw) continue;
      if (!isExplicitAll(raw) && splitMulti(raw).length === 0) {
        msgs.push(`${label} value is invalid.`);
      }
    }

    if (masters) {
      if (role && masters.userTypes?.length) {
        const roleRow = lookupMasterRow(
          masters.userTypes,
          role,
          ["userTypeCode", "UserTypeCode", "USERTYPECODE"],
          ["userTypeName", "UserTypeName", "USERTYPENAME"]
        );
        if (!roleRow) msgs.push(MSG.notFoundRole);
      }

      let marketCodes = [];
      if (market && masters.markets?.length) {
        const mkt = resolveNamedTokens(
          masters.markets,
          market,
          ["marketCode", "MARKETCODE", "MarketCode"],
          ["marketName", "MARKETNAME", "MarketName"],
          isExplicitAll
        );
        marketCodes = mkt.codes;
        if (!mkt.skipped && (mkt.missing.length || !mkt.codes.length)) {
          msgs.push(MSG.notFoundMarket);
        }
      }

      // Domestic+Export add-on: include existing user markets when resolving BV
      if (existing?.market) {
        const extra = String(existing.market)
          .split(/[,;|]/)
          .map((s) => s.trim())
          .filter(Boolean);
        marketCodes = [...new Set([...marketCodes, ...extra])];
      }

      if (bv && masters.businessVerticals?.length) {
        const bvRes = resolveNamedTokens(
          masters.businessVerticals,
          bv,
          BV_CODE_KEYS,
          BV_NAME_KEYS,
          isExplicitAll,
          marketCodes
        );
        if (!bvRes.skipped && (bvRes.missing.length || !bvRes.codes.length)) {
          msgs.push(MSG.notFoundCategory);
        }
      }

      if (/^plant$/i.test(userSpec)) {
        const plantRaw = String(row.plantIds || "").trim();
        if (!plantRaw || plantRaw === "-" || plantRaw.toLowerCase() === "n/a") {
          msgs.push(MSG.plantRequired);
        } else if (masters.plants?.length) {
          const plantRes = resolveNamedTokens(
            masters.plants,
            plantRaw,
            ["plantCode", "PlantCode", "PLANTCODE"],
            ["plantName", "PlantName", "PLANTNAME"],
            isExplicitAll
          );
          if (!plantRes.skipped && (plantRes.missing.length || !plantRes.codes.length)) {
            msgs.push(MSG.notFoundPlant);
          }
        }
      }

      if (/^rdc$/i.test(userSpec)) {
        const rdcRaw = String(row.rdcIds || row.plantIds || "").trim();
        if (!rdcRaw || rdcRaw === "-" || rdcRaw.toLowerCase() === "n/a") {
          msgs.push(MSG.rdcRequired);
        } else if (masters.rdcs?.length) {
          const rdcRes = resolveNamedTokens(
            masters.rdcs,
            rdcRaw,
            ["rdcCode", "RdcCode", "RDCCODE"],
            ["rdcName", "RdcName", "RDCNAME"],
            isExplicitAll
          );
          if (!rdcRes.skipped && (rdcRes.missing.length || !rdcRes.codes.length)) {
            msgs.push(MSG.notFoundRdc);
          }
        }
      }

      if (!isPlantOrRdc) {
        const countryRes = resolveNamedTokens(
          masters.countries || [],
          country,
          ["countryCode", "CountryCode", "COUNTRYCODE"],
          ["countryName", "CountryName", "COUNTRYNAME"],
          isExplicitAll
        );
        if (
          country &&
          !isExplicitAll(country) &&
          !countryRes.skipped &&
          (countryRes.missing.length || !countryRes.codes.length)
        ) {
          msgs.push(MSG.notFoundCountry);
        }

        const zoneRes = resolveNamedTokens(
          masters.zones || [],
          zone,
          ["zoneCode", "ZoneCode", "ZONECODE"],
          ["zoneName", "ZoneName", "ZONENAME"],
          isExplicitAll
        );
        if (
          zone &&
          !isExplicitAll(zone) &&
          !zoneRes.skipped &&
          (zoneRes.missing.length || !zoneRes.codes.length)
        ) {
          msgs.push(MSG.notFoundZone);
        }

        const regionRes = resolveNamedTokens(
          masters.regions || [],
          region,
          ["regionCode", "RegionCode", "REGIONCODE"],
          ["regionName", "RegionName", "REGIONNAME"],
          isExplicitAll
        );
        if (
          region &&
          !isExplicitAll(region) &&
          !regionRes.skipped &&
          (regionRes.missing.length || !regionRes.codes.length)
        ) {
          msgs.push(MSG.notFoundRegion);
        }

        const terrListRaw = masters.territories || [];
        const terrPool =
          !regionRes.skipped && regionRes.codes.length && terrListRaw.length
            ? terrListRaw.filter((row) => {
                const trc = String(row.regionCode || row.RegionCode || "").trim();
                if (!trc) return true;
                return regionRes.codes.some(
                  (c) => String(c).trim().toUpperCase() === trc.toUpperCase()
                );
              })
            : terrListRaw;
        const terrRes = resolveNamedTokens(
          terrPool,
          territory,
          ["territoryCode", "TerritoryCode", "TERRITORYCODE"],
          ["territoryName", "TerritoryName", "TERRITORYNAME"],
          isExplicitAll
        );
        if (
          territory &&
          !isExplicitAll(territory) &&
          !terrRes.skipped &&
          (terrRes.missing.length || !terrRes.codes.length)
        ) {
          msgs.push(MSG.notFoundTerritory);
        }

        if (
          !zoneRes.skipped &&
          zoneRes.rows.length &&
          !countryRes.skipped &&
          countryRes.codes.length &&
          !childrenBelongToParents(zoneRes.rows, countryRes.codes, [
            "countryCode",
            "CountryCode",
          ])
        ) {
          msgs.push(MSG.zoneNotInCountry);
        }
        if (
          !regionRes.skipped &&
          regionRes.rows.length &&
          !zoneRes.skipped &&
          zoneRes.codes.length &&
          !childrenBelongToParents(regionRes.rows, zoneRes.codes, [
            "zoneCode",
            "ZoneCode",
          ])
        ) {
          msgs.push(MSG.regionNotInZone);
        }
        if (
          !regionRes.skipped &&
          regionRes.rows.length &&
          !countryRes.skipped &&
          countryRes.codes.length &&
          !childrenBelongToParents(regionRes.rows, countryRes.codes, [
            "countryCode",
            "CountryCode",
          ])
        ) {
          msgs.push(MSG.regionNotInCountry);
        }
        if (
          !terrRes.skipped &&
          terrRes.rows.length &&
          !regionRes.skipped &&
          regionRes.codes.length &&
          !childrenBelongToParents(terrRes.rows, regionRes.codes, [
            "regionCode",
            "RegionCode",
          ])
        ) {
          msgs.push(MSG.territoryNotInRegion);
        }
        if (
          !terrRes.skipped &&
          terrRes.rows.length &&
          !zoneRes.skipped &&
          zoneRes.codes.length &&
          !childrenBelongToParents(terrRes.rows, zoneRes.codes, [
            "zoneCode",
            "ZoneCode",
          ])
        ) {
          msgs.push(MSG.territoryNotInZone);
        }
        if (
          !terrRes.skipped &&
          terrRes.rows.length &&
          !countryRes.skipped &&
          countryRes.codes.length &&
          !childrenBelongToParents(terrRes.rows, countryRes.codes, [
            "countryCode",
            "CountryCode",
          ])
        ) {
          msgs.push(MSG.territoryNotInCountry);
        }
      }
    }

    const geoKey = [
      normalizeGeoCode(country),
      normalizeGeoCode(zone),
      normalizeGeoCode(region),
      normalizeGeoCode(territory),
    ]
      .join("|")
      .toUpperCase();

    // Plant/RDC rows clear Country/Zone/Region/Territory, so include Plant/RDC
    // in the duplicate key — different plants for same BV are valid.
    const plantOrRdcKey = isPlantOrRdc
      ? String(
          /^plant$/i.test(userSpec)
            ? row.plantIds || ""
            : row.rdcIds || row.plantIds || ""
        )
          .trim()
          .toUpperCase()
      : "";

    // Same Category + Geo (+ Plant/RDC) twice for same AD ID — one message per combo
    let dupComboNoted = false;
    for (const cat of cats) {
      if (!cat) continue;
      const combo = `${adId.toUpperCase()}|${String(cat).trim().toUpperCase()}|${geoKey}|${plantOrRdcKey}`;
      if (comboSeen.has(combo)) {
        if (!dupComboNoted) {
          const prev = comboSeen.get(combo);
          if (prev !== rowNumber) {
            msgs.push(
              `${MSG.duplicateCategoryGeo} (also on row ${prev}).`
            );
          }
          // same-row duplicate tokens already deduped above
          dupComboNoted = true;
        }
      } else {
        comboSeen.set(combo, rowNumber);
      }
    }

    const roleInfo = masters?.userTypes?.length
      ? resolveUserTypeFromMaster(masters.userTypes, role)
      : null;
    const roleKey = roleInfo?.userTypeCode || role;
    const bvSource = masters?.businessVerticals?.length
      ? resolveBusinessVerticalCodes(cats, masters.businessVerticals, [])
      : cats;
    const plantCodes = /^plant$/i.test(userSpec) ? scopeTokens(row.plantIds) : [];
    const rdcCodes = /^rdc$/i.test(userSpec)
      ? scopeTokens(row.rdcIds || row.plantIds)
      : [];
    const slots = buildRoleScopeSlots({
      roleKey,
      roleLabel: roleInfo?.userTypeName || role,
      bvKeys: bvKeysFromCodes(bvSource, masters?.businessVerticals),
      userSpec,
      mappings: isPlantOrRdc
        ? []
        : mappingsFromGeoFields(country, zone, region, territory, masters),
      plantCodes,
      rdcCodes,
      ownerId: existing?.id || "",
      ownerAd: adId,
      ownerName: fullName,
      rowNumber,
    });
    const scopeConflict = findRoleScopeConflict({ slots }, occupiedScopes);
    if (scopeConflict) msgs.push(scopeConflict);
    occupiedScopes.push(...slots);

    if (msgs.length) {
      errors.push({
        rowNumber,
        message: msgs.join(" "),
        row,
      });
    }
  }

  return errors;
}

/**
 * Build a client-side validation error Excel (.xlsx) for bulk upload failures.
 */
export async function buildBulkValidationErrorExcel(failedItems) {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("Validation Errors");
  const headers = [
    "RowNumber",
    "AD ID",
    "Employee Code",
    "User Full Name",
    "Email",
    "Role",
    "Market",
    "Country",
    "Zone",
    "Region",
    "Territory",
    "Business Vertical",
    "Error",
  ];
  headers.forEach((h, i) => {
    const cell = ws.getRow(1).getCell(i + 1);
    cell.value = h;
    cell.font = { bold: true };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFFFCDD2" },
    };
  });

  (failedItems || []).forEach((item, idx) => {
    const r = item.row || {};
    const row = ws.getRow(idx + 2);
    const vals = [
      item.rowNumber ?? r.rowNumber ?? "",
      r.adId || "",
      r.employeeCode || "",
      r.fullName || "",
      r.email || "",
      r.role || "",
      r.market || "",
      r.country || "",
      r.zone || "",
      r.region || "",
      r.territory || "",
      r.businessVertical || "",
      item.message || "",
    ];
    vals.forEach((v, i) => {
      row.getCell(i + 1).value = v;
    });
  });

  ws.columns.forEach((c) => {
    c.width = 18;
  });
  ws.getColumn(13).width = 50;

  const buffer = await wb.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

/**
 * Build Success/Failed Excel from bulk-upload API row results (client-side fallback).
 * @param {Array} rows API row results
 * @param {"success"|"failed"} mode
 */
export async function buildBulkUploadResultExcel(rows, mode = "failed") {
  const wb = new ExcelJS.Workbook();
  const wantSuccess = mode === "success";
  const ws = wb.addWorksheet(wantSuccess ? "Success" : "Errors");
  const headers = [
    "RowNumber",
    "AD ID / User",
    "Employee ID",
    "Email",
    "Action",
    "Message",
  ];
  headers.forEach((h, i) => {
    const cell = ws.getRow(1).getCell(i + 1);
    cell.value = h;
    cell.font = { bold: true };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: wantSuccess ? "FFC8E6C9" : "FFFFCDD2" },
    };
  });

  const filtered = (rows || []).filter((r) => {
    const ok = r?.success === true || r?.Success === true;
    return wantSuccess ? ok : !ok;
  });

  filtered.forEach((r, idx) => {
    const row = ws.getRow(idx + 2);
    const vals = [
      r.rowNumber ?? r.RowNumber ?? "",
      r.userName ?? r.UserName ?? "",
      r.employeeCode ?? r.EmployeeCode ?? "",
      r.email ?? r.Email ?? "",
      r.action ?? r.Action ?? (wantSuccess ? "Success" : "Failed"),
      r.message ?? r.Message ?? "",
    ];
    vals.forEach((v, i) => {
      row.getCell(i + 1).value = v;
    });
  });

  ws.columns.forEach((c) => {
    c.width = 18;
  });
  ws.getColumn(6).width = 60;

  const buffer = await wb.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

export const USER_CATEGORY_GEO_MESSAGES = MSG;
